import React from 'react';
import { useNavigate } from 'react-router-dom';
import { chantiers, getClientById } from '../utils/mockData';
import { useAuth } from '../utils/auth';
import { MapPin, Camera, CheckCircle, ChevronRight, Briefcase } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/StatusBadge';
import { motion } from 'framer-motion';
export function TechnicienDashboard() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  // Get assigned chantiers
  const mesChantiers = user?.role === 'chef_equipe' ? chantiers.filter(c => c.chefEquipeId === user.id || c.team.some(t => t.id === user.id)) : chantiers.filter(c => c.team.some(t => t.id === user?.id));
  const chantiersActifs = mesChantiers.filter(c => ['en_cours', 'planifie', 'en_preparation'].includes(c.status));
  const totalPhotos = mesChantiers.reduce((sum, c) => sum + c.tasks.reduce((s, t) => s + t.images.length, 0), 0);
  const pendingValidations = user?.role === 'chef_equipe' ? chantiers.filter(c => c.chefEquipeId === user.id).reduce((count, c) => count + c.tasks.filter(t => t.validationStatus === 'pending').length, 0) : 0;
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-985">
      {/* Header - Gradient */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-[#1DBAB4] to-[#17A39B] text-white px-4 pt-6 pb-8 shadow-md" data-id="element-986">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-987">
          <h1 className="text-2xl font-bold mb-1" data-id="element-988">
            Bonjour, {user?.name.split(' ')[0]} 👋
          </h1>
          <p className="text-sm text-teal-100" data-id="element-989">
            {user?.role === 'chef_equipe' ? 'Vos chantiers et validations' : 'Vos chantiers actifs'}
          </p>
        </motion.div>
      </div>

      <div className="px-4 -mt-4 space-y-4" data-id="element-990">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3" data-id="element-991">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1
        }} data-id="element-992">
            <Card hover={false} className="p-4 bg-white" data-id="element-993">
              <div className="flex items-center gap-3" data-id="element-994">
                <div className="p-2.5 bg-teal-50 rounded-lg" data-id="element-995">
                  <Briefcase className="w-5 h-5 text-[#1DBAB4]" data-id="element-996" />
                </div>
                <div data-id="element-997">
                  <p className="text-2xl font-bold text-gray-900" data-id="element-998">
                    {chantiersActifs.length}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-999">Chantiers</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} data-id="element-1000">
            <Card hover={false} className="p-4 bg-white" data-id="element-1001">
              <div className="flex items-center gap-3" data-id="element-1002">
                <div className="p-2.5 bg-green-50 rounded-lg" data-id="element-1003">
                  <Camera className="w-5 h-5 text-green-600" data-id="element-1004" />
                </div>
                <div data-id="element-1005">
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1006">
                    {totalPhotos}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1007">Photos</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Pending Validations for Chef d'Équipe */}
        {user?.role === 'chef_equipe' && pendingValidations > 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-1008">
            <Card hover={false} className="p-4 bg-amber-50 border-amber-200 cursor-pointer" onClick={() => navigate('/validations')} data-id="element-1009">
              <div className="flex items-center justify-between" data-id="element-1010">
                <div className="flex items-center gap-3" data-id="element-1011">
                  <CheckCircle className="w-5 h-5 text-amber-600" data-id="element-1012" />
                  <div data-id="element-1013">
                    <p className="font-semibold text-amber-900" data-id="element-1014">
                      {pendingValidations} tâche
                      {pendingValidations > 1 ? 's' : ''} à valider
                    </p>
                    <p className="text-xs text-amber-700" data-id="element-1015">
                      En attente de votre validation
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-600" data-id="element-1016" />
              </div>
            </Card>
          </motion.div>}

        {/* Chantiers List */}
        <div className="space-y-3" data-id="element-1017">
          <h2 className="text-lg font-bold text-gray-900 px-1" data-id="element-1018">
            Mes Chantiers
          </h2>

          {chantiersActifs.length > 0 ? chantiersActifs.map((chantier, index) => {
          const client = getClientById(chantier.clientId);
          return <motion.div key={chantier.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1 * index
          }} data-id="element-1019">
                  <Card className="overflow-hidden active:scale-[0.98] transition-transform" onClick={() => navigate(`/chantiers/${chantier.id}`)} data-id="element-1020">
                    <div className="p-4" data-id="element-1021">
                      <div className="flex items-start justify-between gap-2 mb-3" data-id="element-1022">
                        <div className="flex-1 min-w-0" data-id="element-1023">
                          <h3 className="font-bold text-gray-900 text-sm line-clamp-2 leading-tight mb-1" data-id="element-1024">
                            {chantier.name}
                          </h3>
                          {client && <p className="text-xs text-gray-600 mb-1" data-id="element-1025">
                              {client.raisonSociale}
                            </p>}
                          <div className="flex items-center text-xs text-gray-500" data-id="element-1026">
                            <MapPin size={12} className="mr-1 flex-shrink-0" data-id="element-1027" />
                            <span className="truncate" data-id="element-1028">{chantier.city}</span>
                          </div>
                        </div>
                        <StatusBadge status={chantier.status} size="sm" data-id="element-1029" />
                      </div>

                      <div className="flex items-center justify-between" data-id="element-1030">
                        <div className="flex items-center gap-3 text-xs text-gray-600" data-id="element-1031">
                          <div className="flex items-center gap-1" data-id="element-1032">
                            <Camera size={12} data-id="element-1033" />
                            <span data-id="element-1034">
                              {chantier.tasks.reduce((s, t) => s + t.images.length, 0)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1" data-id="element-1035">
                            <CheckCircle size={12} data-id="element-1036" />
                            <span data-id="element-1037">{chantier.progress}%</span>
                          </div>
                        </div>
                        <Button size="sm" className="h-7 px-3 text-xs" onClick={e => {
                    e.stopPropagation();
                    navigate(`/chantiers/${chantier.id}`);
                  }} data-id="element-1038">
                          Voir détails
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>;
        }) : <Card hover={false} className="p-8 text-center" data-id="element-1039">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-1040">
                <MapPin className="text-gray-400" size={24} data-id="element-1041" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-1042">
                Aucun chantier actif
              </h3>
              <p className="text-xs text-gray-500" data-id="element-1043">
                Vous n'êtes pas assigné à un chantier pour le moment
              </p>
            </Card>}
        </div>
      </div>
    </div>;
}